import type { ConvertOptions } from "@kensio/pinyinjs";

/**
 * The conversion settings, and the controls that move them.
 *
 * One entry per field of the package's `ConvertOptions`, but described for
 * somebody learning or teaching Chinese rather than for somebody calling the
 * function: the label on the third-tone control says what it does to 你好,
 * not that it is `sandhi.thirdTone`.
 *
 * The defaults are the package's own, which are also the right ones for this
 * audience — standard orthography, as 汉语拼音正词法基本规则 defines it.
 */

/**
 * Every setting the tools expose, with none of them optional.
 *
 * `ConvertOptions` makes each field optional, because leaving one out is how a
 * caller asks for the default. A set of controls needs the opposite: a radio
 * group has to know which of its values is currently on. {@link asConvertOptions}
 * converts back.
 */
export interface ToolOptions {
  readonly locale: "zh-CN" | "zh-TW";
  readonly notation: "marks" | "numbers" | "superscript" | "none";
  readonly apostrophe: "always" | "standard" | "never";
  readonly capitals: "auto" | "proper" | "none";
  readonly punctuation: "latin" | "keep";
  readonly grouping: boolean;
  readonly numbers: "read" | "keep";
  readonly yiBu: boolean;
  readonly thirdTone: boolean;
}

/**
 * What the tools start on.
 */
export const defaultOptions: ToolOptions = {
  locale: "zh-CN",
  notation: "marks",
  apostrophe: "always",
  capitals: "auto",
  punctuation: "latin",
  grouping: true,
  numbers: "read",
  yiBu: true,
  thirdTone: false,
};

/**
 * The settings as the package wants them, with anything left at its default
 * left out.
 *
 * The trimming matters for `sandhi`, which is merged with its defaults rather
 * than replacing them: passing `{}` and passing nothing have to mean the same
 * thing here as they do in the package.
 */
export function asConvertOptions(options: ToolOptions): ConvertOptions {
  const changed: Record<string, unknown> = {};

  for (const field of [
    "locale",
    "notation",
    "apostrophe",
    "capitals",
    "punctuation",
    "grouping",
    "numbers",
  ] as const) {
    if (options[field] !== defaultOptions[field]) {
      changed[field] = options[field];
    }
  }

  const sandhi = {
    ...(options.yiBu !== defaultOptions.yiBu && { yiBu: options.yiBu }),
    ...(options.thirdTone !== defaultOptions.thirdTone && {
      thirdTone: options.thirdTone,
    }),
  };
  if (Object.keys(sandhi).length > 0) {
    changed["sandhi"] = sandhi;
  }

  return changed;
}

/** Whether every setting is where it started. */
export function isDefault(options: ToolOptions): boolean {
  return Object.keys(asConvertOptions(options)).length === 0;
}
